
import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  FormField,
  FormItem,
  FormControl,
} from '@/components/ui/form';
import { LineItemFormValues } from '../hooks/useLineItemFormSchema';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';
import MarkupTiersTable from './MarkupTiersTable';
import MarkupTierForm from './MarkupTierForm';
import TierDescription from './TierDescription';
import { usePricingRefresh } from '@/hooks/usePricingRefresh';

interface CustomMarkupTiersProps {
  form: UseFormReturn<LineItemFormValues>;
  baseCost: number;
}

const CustomMarkupTiers = ({ form, baseCost }: CustomMarkupTiersProps) => {
  // Access tiers and use_custom_tiers from form
  const customTiersData = form.watch('custom_tiers') || [];
  const useCustomTiers = form.watch('use_custom_tiers') || false;
  const lineItemId = form.getValues('id');
  
  // Convert form data to LineItemMarkupTier format, ensuring required fields are present
  const customTiers: LineItemMarkupTier[] = customTiersData.filter(tier => 
    tier.min_amount !== undefined && 
    tier.markup_percentage !== undefined && 
    tier.margin_percentage !== undefined
  ).map(tier => ({
    id: tier.id || `temp-${Date.now()}`,
    min_amount: tier.min_amount!,
    max_amount: tier.max_amount || null,
    markup_percentage: tier.markup_percentage!,
    margin_percentage: tier.margin_percentage!,
    line_item_id: tier.line_item_id || undefined
  }));

  // Auto-refresh pricing when custom tiers are edited (but only for existing line items)
  const { refreshPricing } = usePricingRefresh({
    lineItemId: lineItemId || '',
    currentBaseCost: baseCost,
    categoryId: form.getValues('category_id'),
    companyId: '', // Will be populated by the hook
    customTiers: customTiers,
    useCustomTiers: useCustomTiers
  });

  // Auto-refresh trigger for custom tier changes
  useEffect(() => {
    if (lineItemId && useCustomTiers) {
      // Trigger auto-refresh when custom tiers are modified
      const triggerAutoRefresh = async () => {
        try {
          await refreshPricing('custom_tier_edit');
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        }
      };

      // Debounce the auto-refresh to avoid excessive calls
      const timeoutId = setTimeout(triggerAutoRefresh, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [customTiers, useCustomTiers, lineItemId, refreshPricing]);
  
  // Helper function to validate if tiers are meaningful
  const hasValidTiers = (tiers: LineItemMarkupTier[]): boolean => {
    if (!tiers || tiers.length === 0) return false;
    
    // Check if any tier has meaningful bounds (not all zeros)
    return tiers.some(tier => 
      tier.min_amount > 0 || 
      (tier.max_amount !== null && tier.max_amount > 0) ||
      (tier.min_amount === 0 && tier.max_amount === null) // 0+ bracket is valid
    );
  };
  
  const handleAddTier = (newTier: LineItemMarkupTier) => {
    // Add new tier to the form, converting from LineItemMarkupTier to form format
    const updatedTiers = [...customTiersData, {
      id: newTier.id,
      line_item_id: newTier.line_item_id || undefined,
      min_amount: newTier.min_amount,
      max_amount: newTier.max_amount,
      markup_percentage: newTier.markup_percentage,
      margin_percentage: newTier.margin_percentage
    }];
    
    // Sort tiers by min_amount
    updatedTiers.sort((a, b) => (a.min_amount || 0) - (b.min_amount || 0));
    
    form.setValue('custom_tiers', updatedTiers);
  };
  
  const handleRemoveTier = (index: number) => {
    const updatedTiers = customTiersData.filter((_, i) => i !== index);
    form.setValue('custom_tiers', updatedTiers);
  };

  const handleUpdateTier = (index: number, updatedTier: LineItemMarkupTier) => {
    const updatedTiers = [...customTiersData];
    updatedTiers[index] = {
      id: updatedTier.id,
      line_item_id: updatedTier.line_item_id || undefined,
      min_amount: updatedTier.min_amount,
      max_amount: updatedTier.max_amount,
      markup_percentage: updatedTier.markup_percentage,
      margin_percentage: updatedTier.margin_percentage
    };
    
    // Sort tiers by min_amount after update
    updatedTiers.sort((a, b) => (a.min_amount || 0) - (b.min_amount || 0));
    
    form.setValue('custom_tiers', updatedTiers);
  };
  
  // Toggle custom tiers usage
  const toggleCustomTiers = (checked: boolean) => {
    form.setValue('use_custom_tiers', checked);
    
    // If enabling custom tiers, update the pricing source
    if (checked) {
      form.setValue('pricing_source', 'custom');
    } else {
      // If disabling, determine if we should use category or global
      const hasCategory = !!form.getValues('category');
      form.setValue('pricing_source', hasCategory ? 'category' : 'global');
    }
  };

  // Find the applicable tier based on the base cost
  const findApplicableTier = (amount: number): LineItemMarkupTier | null => {
    if (!customTiers || customTiers.length === 0) return null;
    
    // Apply the same inclusive lower / exclusive upper bound logic
    return customTiers.find(tier => 
      amount >= tier.min_amount && 
      (tier.max_amount === null || amount < tier.max_amount)
    ) || null;
  };

  const applicableTier = useCustomTiers ? findApplicableTier(baseCost) : null;
  const tiersAreValid = hasValidTiers(customTiers);
  
  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Custom Markup/Margin Tiers</CardTitle>
          <CardDescription>
            Define price-based custom markup/margin tiers for this line item
          </CardDescription>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Enable Custom Tiers</span>
          <FormField
            control={form.control}
            name="use_custom_tiers"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Switch 
                    checked={field.value}
                    onCheckedChange={toggleCustomTiers}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </CardHeader>
      <CardContent>
        {useCustomTiers ? (
          <>
            {/* Validation warning for invalid tiers */}
            {useCustomTiers && customTiers.length > 0 && !tiersAreValid && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  ⚠️ Warning: All custom tiers have zero bounds and will be treated as inactive. 
                  The system will fall back to category or global tiers.
                </p>
              </div>
            )}
            
            <TierDescription baseCost={baseCost} applicableTier={applicableTier} />
            
            <MarkupTiersTable 
              tiers={customTiers} 
              onRemoveTier={handleRemoveTier}
              onUpdateTier={handleUpdateTier}
              editable={true}
            />
            
            <MarkupTierForm onAddTier={handleAddTier} />
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground">
              Custom tiers are disabled. Toggle the switch above to enable them.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              When disabled, the system will use category tiers (if category is selected) or global tiers as fallback.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomMarkupTiers;
